import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  LuCompass,
  LuSparkles,
  LuCpu,
  LuBot,
  LuSettings2,
  LuChevronDown,
  LuMenu,
  LuX,
  LuGlobe,
  LuSmartphone,
  LuCode,
  LuPenTool,
  LuServer,
  LuCloud,
  LuLayoutDashboard,
  LuWrench,
} from "react-icons/lu";
import Logo from "./Logo";

const Navbar: React.FC = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [desktopSolutionsOpen, setDesktopSolutionsOpen] = useState(false);


  useEffect(() => {
    if (!isMenuOpen) {
      setMobileSolutionsOpen(false);
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  const navbarClasses = `fixed top-0 w-full z-50 transition-all duration-300 py-1.5 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-white"}`;

  const textColorClass = "text-gray-800 hover:text-[#0B25E6]";
  const activeColorClass = "text-[#0B1538] font-semibold";

  const solutionsItems = [
    {
      name: "Website Development",
      path: "/services/web-development",
      icon: LuGlobe,
    },
    {
      name: "Mobile Application Development",
      path: "/services/mobile-development",
      icon: LuSmartphone,
    },
    {
      name: "Custom Software Development",
      path: "/services/custom-software",
      icon: LuCode,
    },
    { name: "UI/UX Design", path: "/services/ui-ux-design", icon: LuPenTool },

    { name: "Generative AI", path: "/generative-ai", icon: LuSparkles },
    { name: "Autonomous Agents", path: "/autonomous-agents", icon: LuBot },
    {
      name: "AI Workflow Automation",
      path: "/ai-workflow-automation",
      icon: LuSettings2,
    },
  ];

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Solutions", path: "/solutions" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <nav className={navbarClasses}>
      <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex justify-center items-center h-16">
          <div className="absolute left-0 flex items-center">
            <Link to="/" className="flex items-center">
              <Logo textColor="text-[#0B1538]" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((item) => {
              const label = item.name === "Contact" ? "Contact Us" : item.name;

              if (item.name === "Solutions") {
                const isSolutionsActive =
                  (location.pathname.includes("solutions") ||
                    solutionsItems.some(
                      (sol) => sol.path === location.pathname,
                    )) &&
                  location.pathname !== "/services";
                return (
                  <div
                    key={item.name}
                    className="relative group"
                    onMouseEnter={() => setDesktopSolutionsOpen(true)}
                    onMouseLeave={() => setDesktopSolutionsOpen(false)}
                  >
                    <button
                      className={`flex items-center space-x-1 outline-none ${isSolutionsActive ? activeColorClass : textColorClass} font-medium text-md transition-colors`}
                    >
                      <span>{label}</span>
                      {React.createElement(LuChevronDown as any, {
                        className: `w-4 h-4 transition-transform duration-200 ${desktopSolutionsOpen ? "rotate-180" : ""}`,
                      })}
                    </button>

                    {/* Dropdown Menu */}
                    <div
                      className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 transition-all duration-300 ease-out transform ${desktopSolutionsOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-2"}`}
                    >
                      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-2 overflow-hidden">
                        <div className="flex flex-col space-y-1">
                          {solutionsItems.map((solution) => (
                            <Link
                              key={solution.name}
                              to={solution.path}
                              onClick={() => setDesktopSolutionsOpen(false)}
                              className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-colors group/item"
                            >
                              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover/item:bg-blue-600 group-hover/item:text-white transition-colors">
                                {React.createElement(solution.icon as any, {
                                  className: "w-5 h-5",
                                })}
                              </div>
                              <span className="text-gray-700 font-medium group-hover/item:text-blue-600 transition-colors">
                                {solution.name}
                              </span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center space-x-1 transition-colors relative group text-md font-medium ${isActive ? activeColorClass : textColorClass}`
                  }
                >
                  <span>{label}</span>
                  <span
                    className="absolute bottom-0 left-0 w-full h-0.5 transform scale-x-0 transition-transform duration-300 origin-center group-hover:scale-x-100 bg-[#0B25E6]"
                  ></span>
                </NavLink>
              );
            })}
          </div>

          {/* Desktop Book a Demo Button */}
          <div className="hidden lg:flex absolute right-0 items-center">
            <Link
              to="/contact"
              className="px-6 py-2.5 bg-[#0B25E6] text-white hover:bg-blue-800 transition-colors font-semibold"
            >
              Book a Demo
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="absolute right-0 lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-800 hover:text-[#0B25E6] focus:outline-none transition-colors"
            >
              {isMenuOpen
                ? React.createElement(LuX as any, { className: "h-6 w-6" })
                : React.createElement(LuMenu as any, { className: "h-6 w-6" })}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white rounded-b-2xl shadow-xl border-t border-gray-100 absolute top-16 left-0 w-full overflow-hidden">
            <div className="px-4 pt-2 pb-4 space-y-1">
              {navLinks.map((item) => {
                const label =
                  item.name === "Contact" ? "Contact Us" : item.name;

                if (item.name === "Solutions") {
                  const isSolutionsActive =
                    location.pathname.includes("solutions") ||
                    solutionsItems.some(
                      (sol) => sol.path === location.pathname,
                    );
                  return (
                    <div key={item.name} className="flex flex-col">
                      <button
                        onClick={() =>
                          setMobileSolutionsOpen(!mobileSolutionsOpen)
                        }
                        className={`flex items-center justify-between w-full px-3 py-3 rounded-lg text-base font-medium transition-colors ${mobileSolutionsOpen || isSolutionsActive ? "text-primary" : "text-gray-700 hover:bg-gray-50 hover:text-primary"}`}
                      >
                        <span className="flex items-center space-x-3">
                          <span>{label}</span>
                        </span>
                        {React.createElement(LuChevronDown as any, {
                          className: `w-5 h-5 transition-transform duration-200 ${mobileSolutionsOpen ? "rotate-180" : ""}`,
                        })}
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${mobileSolutionsOpen ? "max-h-96 opacity-100 mt-1" : "max-h-0 opacity-0"}`}
                      >
                        <div className="space-y-1 pl-4">
                          {solutionsItems.map((solution) => (
                            <NavLink
                              key={solution.name}
                              to={solution.path}
                              onClick={() => setIsMenuOpen(false)}
                              className={({ isActive }) =>
                                `flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-gray-600 hover:text-primary hover:bg-gray-50"}`
                              }
                            >
                              {React.createElement(solution.icon as any, {
                                className: "w-5 h-5 opacity-70",
                              })}
                              <span>{solution.name}</span>
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-3 py-3 rounded-lg text-base font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-gray-700 hover:bg-gray-50 hover:text-primary"}`
                    }
                  >
                    <span>{label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
